<template lang="pug">
button.gtm-category.game-room.relative.cursor-pointer(type="button", :data-location="parent ? parent.category : ''", :data-icon="room.category", @click="enter")
  .pointer-events-none.game-room
    //- 有從後台上傳館別圖
    template(v-if="isUploadRoomImage")
      img(:src="room.entranceImage", :alt="room.name ? room.name[locale] || room.name['en-US'] : ''", class="landscape:xl:w-full")
    template(v-else)
      //- 沒有從後台上傳館別圖
      img(:src="indexSeriesImgNormal", :alt="room.category", class="landscape:xl:w-full")
      //- 如果是自動上架館會顯示預設圖
      .img-box(v-if="isAutoRoom")
        .provider-background
          img.img-position(:src="icon", :alt="room.name ? room.name[locale] || room.name['en-US'] : ''", class="landscape:xl:w-full")
      //- 有圖就沒字，有字就是沒圖
      .img-box(v-else)
        .absolute.inset-0.flex.px-4
          .room-name.m-auto.px-2.text-center.text-primary-text.text-lg {{ room.name ? room.name[locale] || room.name['en-US'] : '' }}
</template>

<script setup>
import indexSeriesImgNormal from 'assets/images/game/index_series_img_normal.png'
import urlJoin from 'url-join'
import { computed } from 'vue'

import { useNuxtApp, useRuntimeConfig } from '#app'
import { useI18n } from '#imports'
import { useMainStore } from '@/stores/main'
import * as constant from '@/utils/constants'

const props = defineProps({
  room: {
    type: Object,
    required: true,
  },
  parent: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['enter'])

const config = useRuntimeConfig()
const { $SoundControl, $analytics } = useNuxtApp()
const { locale } = useI18n()
const store = useMainStore()
const { getBackCdnDefaultUrl, getBackCdnUrl } = useCdnAssets()

const getProviderInfo = computed(() => store.getProviderInfo)

const providerList = computed(() => getProviderInfo.value || [])
const gameTypeList = computed(() => constant.GAME_TYPE_LIST)

const roomName = computed(() => (props.room.name ? props.room.name['en-US'] : props.room.category || 'UNDEFINED_NAME').toLowerCase())

const isProvider = computed(() => {
  return (
    providerList.value?.some(
      (provider) =>
        roomName.value === provider.providerId.toLowerCase() ||
        roomName.value === provider.providerName.toLowerCase() ||
        roomName.value === provider.providerName.replace(/\s/g, '').toLowerCase()
    ) || false
  )
})

const isGameType = computed(() =>
  gameTypeList.value.some((game) => roomName.value.includes(game.typeId.toLowerCase()) || roomName.value.includes(game.name.toLowerCase()))
)

const icon = computed(() => {
  if (isProvider.value) {
    const currentName =
      providerList.value.find((provider) => roomName.value === provider.providerId.toLowerCase())?.providerId ||
      providerList.value.find((provider) => roomName.value === provider.providerName.toLowerCase())?.providerId ||
      providerList.value.find((provider) => roomName.value === provider.providerName.replace(/\s/g, '').toLowerCase())?.providerId
    return getBackCdnDefaultUrl(`games/providers_icon/providers_icon_${currentName.toLowerCase()}_normal@2x.png`)
  } else if (isGameType.value) {
    const currentType =
      gameTypeList.value.find((game) => roomName.value.includes(game.typeId.toLowerCase())).typeId ||
      gameTypeList.value.find((game) => roomName.value.includes(game.name.toLowerCase())).typeId
    return getBackCdnDefaultUrl(`games/types_icon/type_icon_${currentType.toLowerCase()}_normal@2x.png`)
  }
  return null
})

const hasRoomImage = computed(() => props.room.image)

const isUploadRoomImage = computed(() => props.room?.entranceImage)

const isCustomRoom = computed(() => !isProvider.value && !isGameType.value)

const isAutoRoom = computed(() => props.room?.isAutoUpload || false)

const enter = () => {
  $SoundControl.sound.play('click')
  $analytics.push({
    event: 'lobby_click',
    location: props.parent?.name?.['en-US'],
    locationId: props.parent?.categoryId,
    icon: props.room?.name?.['en-US'],
    iconId: props.room.categoryId,
  })
  emit('enter', props.room)
}
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

.game-room {
  width: 9.375rem;
  height: 6.875rem;
  @apply transition hover:scale-95 portrait:w-full portrait:h-auto;

  @media screen and (max-height: 300px) {
    width: 5rem !important;
    height: 5rem;
  }

  @media (min-width: 1024px) and (orientation: landscape) {
    width: 100%;
    aspect-ratio: 15/11;
    height: auto;
    justify-self: center;
  }
}

.room-name {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  white-space: normal;
  text-overflow: ellipsis;
  overflow: hidden;
}

.provider-background {
  width: 100%;
  height: 35%;
  position: absolute;
  top: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(to left, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
}

[dir='rtl'] .provider-background {
  transform: translate(50%, -50%);
}

.img-box {
  width: 100%;
  max-width: 300px;
  height: 100%;
  max-height: 220px;
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  overflow: hidden;
  @media (min-width: 1024px) and (orientation: landscape) {
    max-width: none;
    max-height: none;
  }
}

.img-position {
  position: absolute;
  width: 100%;
  top: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%) scale(0.6);
}

[dir='rtl'] .img-position {
  transform: translate(50%, -50%) scale(0.6);
}
</style>
